import { Flex, Box, Input, InlineBanner, Text, FormControl } from '@cad-arc';
import { MergeEventView } from '@mark43/cad-api';
import { Observer, observer } from 'mobx-react-lite';
import React, { useCallback } from 'react';
import { GroupedVirtuoso, Components } from 'react-virtuoso';
import { ContextMenuStateProvider } from '../../../../../queues/context/context-menu-state-provider';
import { EventTile } from '../../../../../common/components/event-tile';
import { TileEvent } from '../../../../../common/types';
import { useKeyboardNavigation } from '../../../../../common/hooks/use-tile-keyboard-navigation';
import {
    ManageEventMergeStore,
    MergeEventGroupEnum,
    MergeEventGroupEnumType,
} from '../../store/manage-merge-event-store';
import { useEnabledFeatures } from '../../../../../common/hooks/enabled-features/use-enabled-features';

export interface ManageEventMergeManagerProps {
    store: ManageEventMergeStore;
    interactionDisabled: boolean;
    error: ManageEventMergeModalError | null;
    onSetUnmergeComments: (comment: string) => void;
}

export type ManageEventMergeModalError = { title: string; message: string };

const modalBodyPadding = 'var(--arc-space-6)';
const inverseModalPadding = `calc(-1 * ${modalBodyPadding})`;
const inverseModalPaddingBottom = `calc(-1 * var(--arc-space-2))`;

export const ManageEventMergeManager: React.FC<ManageEventMergeManagerProps> = observer(
    ({ store, error, interactionDisabled, onSetUnmergeComments }) => {
        const enabledFeatures = useEnabledFeatures();
        const { groupCounts, groupHeaders, list } = store.data;

        const currentPrimaryEvent = store.currentPrimaryEvent;
        const wasPrimaryEventCleared: boolean = store.currentPrimaryEvent.wasCleared;
        const currentPrimaryEventId = store.currentPrimaryEvent.event.id;
        const showUnmergeComments = store.getUnmergeItems().length !== 0;

        // Property access for mobx update. (required, Otherwise mobx doesn't update and state doesn't change)
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        groupCounts.length;
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        groupHeaders.length;
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        list.length;

        const deselectAllChildrenEvents = (): void =>
            store.setSelectedItems([currentPrimaryEventId]);

        const onSelectEvent = (item: MergeEventView | TileEvent): void => {
            const shouldUnmergeAllChildren = item.id === currentPrimaryEventId;

            if (shouldUnmergeAllChildren) {
                deselectAllChildrenEvents();
            } else {
                store.toggleItemSelection(item.id);
            }
        };

        const onSetPrimaryEvent = (item: MergeEventView | TileEvent): void => {
            store.updatePrimaryEvent(item);
        };

        const canFocusPrimary = useCallback(
            (id: number) => {
                return store.canFocusPrimary(id);
            },
            [store]
        );

        const keyboardTileNavigation = useKeyboardNavigation({
            store,
            onSelectItem: onSelectEvent,
            onSetPrimaryItem: onSetPrimaryEvent,
            canFocusPrimary,
        });

        const renderItemContent = (index: number): React.ReactElement => {
            const item = list[index];
            const group = store
                .getMergeStoreGroupingDirective()
                .getGroupValue(item) as MergeEventGroupEnumType;
            const isDisabledBecauseUserCannotMergeEvents =
                !enabledFeatures.mergeEvents && ['ASSIGNED', 'UNASSIGNED'].includes(group);
            const isDisabledBecauseUserCannotUnmergeEvents =
                !enabledFeatures.unmergeEvents && group === 'ALREADY_MERGED';

            let tooltipOverride;
            if (isDisabledBecauseUserCannotMergeEvents) {
                tooltipOverride = 'You do not have the ability to merge events';
            } else if (isDisabledBecauseUserCannotUnmergeEvents) {
                tooltipOverride = 'You do not have the ability to unmerge events';
            }

            return (
                <Box pr={modalBodyPadding}>
                    <EventTile
                        isDisabled={
                            interactionDisabled ||
                            isDisabledBecauseUserCannotMergeEvents ||
                            isDisabledBecauseUserCannotUnmergeEvents
                        }
                        onSelectEvent={onSelectEvent}
                        onSetPrimaryEvent={onSetPrimaryEvent}
                        event={item}
                        isFocused={index === keyboardTileNavigation.currentIndex}
                        isFocusingLeft={keyboardTileNavigation.isFocusingLeft}
                        isFocusingRight={keyboardTileNavigation.isFocusingRight}
                        isSelected={store.selectedItems[item.id]}
                        isPrimaryEvent={
                            currentPrimaryEventId === item.id && !wasPrimaryEventCleared
                        }
                        hidePrimarySection={!store.canFocusPrimary(item.id)}
                        tooltipOverride={tooltipOverride}
                    />
                </Box>
            );
        };

        const renderGroupHeader = (groupIndex: number): React.ReactNode => {
            return (
                <Observer>
                    {() => {
                        const groupMeta =
                            MergeEventGroupEnum[
                                store.data.groupHeaders[groupIndex] as MergeEventGroupEnumType
                            ];
                        if (!groupMeta) {
                            return <></>;
                        }
                        return (
                            <Box
                                key={groupHeaders[groupIndex]}
                                bg="surface.primary"
                                pt={`calc(${modalBodyPadding} / 2)`}
                                pb={`calc(${modalBodyPadding} / 2)`}
                                position="static"
                            >
                                <Text fontWeight="bold">
                                    {`${groupMeta.displayName} (${store.data.groupCounts[groupIndex]})`}
                                </Text>
                            </Box>
                        );
                    }}
                </Observer>
            );
        };

        return (
            <ContextMenuStateProvider>
                <ManageEventMergeErrorBanner
                    error={error}
                    clearedEventNumber={
                        wasPrimaryEventCleared ? currentPrimaryEvent.event.eventNumber ?? '' : null
                    }
                />
                <Box w="100%">
                    {showUnmergeComments && (
                        <FormControl isRequired label="Unmerging Comments">
                            <Input
                                required
                                data-testid="UNMERGING_COMMENTS"
                                type="text"
                                role="textbox"
                                disabled={interactionDisabled}
                                isClearable={false}
                                onChange={(e: { currentTarget: { value: string } }) =>
                                    onSetUnmergeComments?.(e.currentTarget.value)
                                }
                            />
                        </FormControl>
                    )}
                </Box>
                {list.length === 0 ? (
                    <MergeEventManagerEmptyState />
                ) : (
                    <GroupedVirtuoso
                        groupCounts={groupCounts}
                        ref={keyboardTileNavigation.virtuosoRef}
                        groupContent={renderGroupHeader}
                        style={{
                            height: showUnmergeComments ? 507 : 556,
                            marginBottom: inverseModalPaddingBottom,
                            marginRight: inverseModalPadding,
                            marginTop: 0,
                            ...(interactionDisabled ? { overflow: 'hidden' } : {}),
                        }}
                        components={{ List: MergeEventVirtuosoListWrapper }}
                        itemContent={renderItemContent}
                        overscan={200}
                    />
                )}
            </ContextMenuStateProvider>
        );
    }
);

const MergeEventVirtuosoListWrapper: Components['List'] = React.forwardRef(
    ({ children, style, ...rest }, ref) => {
        return (
            <Box
                ref={ref}
                style={{ ...style }}
                {...rest}
                data-testid="merge-event-manager-list-body"
            >
                {children}
            </Box>
        );
    }
);

const ManageEventMergeErrorBanner: React.FC<{
    error: ManageEventMergeModalError | null;
    clearedEventNumber: string | null;
}> = ({ error, clearedEventNumber }) => {
    if (error !== null) {
        return (
            <Box mb="24px">
                <InlineBanner status="error" title={error.title} description={error.message} />
            </Box>
        );
    }

    if (clearedEventNumber !== null) {
        return (
            <Box mb="24px">
                <InlineBanner
                    status="error"
                    title={`Event ${clearedEventNumber} is no longer active.`}
                    description="Reactivate this event or select a new primary event."
                />
            </Box>
        );
    }

    return null;
};

const MergeEventManagerEmptyState: React.FC = () => (
    <Flex direction="column" pt={modalBodyPadding}>
        <Box position="static">
            <Text fontWeight="bold">Already Merged (0)</Text>
        </Box>
        <Box position="static">
            <Text fontWeight="bold">To Merge (0)</Text>
        </Box>
        <Box position="static">
            <Text fontWeight="bold">Assigned (0)</Text>
        </Box>
        <Box position="static">
            <Text fontWeight="bold">Unassigned (0)</Text>
        </Box>
    </Flex>
);
